import apiClient from './apiClient';
import logger from '../../utils/logger';

import {
  validateInvoiceResponse,
  validatePreviewResponse
} from '../../utils/invoiceSchemas';
import { isInvoiceOverdue } from '../../utils/invoiceHelpers';

/**
 * Invoice service for handling invoice-related API calls
 */
const invoiceService = {
  /**
   * Get all invoices with optional filters
   * @param {Object} params - Query parameters (filters, pagination)
   * @returns {Promise} API response with invoices and pagination
   */
  getInvoices: async (params = {}) => {
    const response = await apiClient.get('/invoices', { params });
    return response.data;
  },

  /**
   * Get invoice statistics
   * @returns {Promise} API response with statistics
   */
  getStatistics: async () => {
    const response = await apiClient.get('/invoices/statistics');
    return response.data;
  },
  /**
   * Get next invoice number
   * @returns {Promise} API response with next invoice number
   */
  getNextInvoiceNumber: async () => {
    const response = await apiClient.get('/invoices/next-number');
    return response.data;
  },
  /**
   * Get a specific invoice by ID
   * @param {string} id - Invoice ID
   * @returns {Promise} API response with invoice details
   */
  getInvoice: async (id) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const response = await apiClient.get(`/invoices/${id}`);
    
    // Fixed: Removed broken validateInvoiceResponse that was stripping items array
    // TODO: Implement proper schema validation that includes items array
    return response.data;
  },

  /**
   * Calculate preview totals for real-time UX
   * Maps frontend camelCase to backend snake_case (GAP #2 compliance)
   * @param {Object} formData - Frontend form data
   * @returns {Promise} API response with preview calculations
   */
  calculatePreviewTotals: async (formData) => {
    try {
      // Send data in camelCase - backend will transform to snake_case via toDatabaseFormat
      const mappedData = {
        items: formData.items?.map(item => ({
          quantity: parseFloat(item.quantity) || 0,
          unitPrice: parseFloat(item.unitPrice) || 0, // Backend transforms to unit_price
          isTaxable: Boolean(item.isTaxable) // Backend transforms to is_taxable
        })) || [],
        taxRate: parseFloat(formData.taxRate) || 0, // Backend transforms to tax_rate
        discountType: formData.discountType || '',
        discountValue: parseFloat(formData.discountValue) || 0,
        shippingCost: parseFloat(formData.shippingCost) || 0
      };
      
      logger.debug('Calculating preview totals', {
        operation: 'calculatePreviewTotals',
        itemCount: mappedData.items.length,
        taxRate: mappedData.taxRate
      });
      
      const response = await apiClient.post('/invoices/preview-calculations', mappedData);
      
      // Phase 6.1: Use comprehensive validation helper with safe defaults
      return validatePreviewResponse(response.data);
    } catch (error) {
      // Use Winston logging for PCI DSS compliance (GAP #3)
      logger.warn('Preview calculation failed, using defaults', {
        error: error.message,
        requestId: error.response?.data?.requestId,
        operation: 'calculatePreviewTotals'
      });
      
      // Return safe defaults for UX continuity
      return {
        success: true,
        preview: {
          subtotal: '0.00',
          taxableSubtotal: '0.00',
          taxAmount: '0.00',
          total: '0.00',
          discount: '0.00',
          shipping: '0.00'
        },
        metadata: {
          calculationType: 'preview',
          authoritative: false,
          error: 'Preview calculation failed - using defaults'
        }
      };
    }
  },

  /**
   * Live evaluate for invoice form (product rules only; server forces isRecurring=false).
   * @param {Object} payload
   * @returns {Promise} { appliedDiscounts, discountTotalCents, totalCents, codeError }
   */
  evaluateDiscounts: async (payload) => {
    const response = await apiClient.post('/discounts/evaluate', {
      context: 'manual_invoice',
      isRecurring: false,
      items: payload.items || [],
      subtotalCents: payload.subtotalCents,
      code: payload.code
    });
    return response.data;
  },

  /**
   * Create a new invoice with enhanced field mapping
   * Maps frontend camelCase to backend snake_case (GAP #2 compliance)
   * @param {Object} invoiceData - Invoice data including items
   * @returns {Promise} API response with created invoice
   */
  createInvoice: async (invoiceData) => {
    try {
      logger.debug('Creating invoice with clean data', {
        operation: 'createInvoice',
        customerId: invoiceData.customerId,
        paymentOptions: invoiceData.paymentOptions,
        itemCount: invoiceData.items?.length || 0
      });
      
      // Send clean camelCase data - backend will transform to snake_case
      const response = await apiClient.post('/invoices', invoiceData);
      
      // Phase 6.1: Use comprehensive schema validation for creation response
      try {
        const validatedResponse = validateInvoiceResponse(response.data);
        
        logger.info('Invoice created successfully with validation', {
          operation: 'createInvoice',
          invoice_id: validatedResponse.invoice.id,
          invoice_number: validatedResponse.invoice.invoiceNumber
        });
        
        return validatedResponse;
      } catch (validationError) {
        logger.error('Invoice creation response validation failed', {
          operation: 'invoiceService.createInvoice',
          error: validationError.message,
          timestamp: new Date().toISOString()
        });
        
        throw new Error(`Invoice creation validation failed: ${validationError.message}`);
      }
    } catch (error) {
      // Winston logging for PCI DSS compliance (GAP #3)
      logger.error('Frontend invoice creation failed', {
        error: error.message,
        requestId: error.response?.data?.requestId,
        operation: 'createInvoice',
        customerId: invoiceData.customerId,
        status: error.response?.status
      });
      
      throw error; // Re-throw for component error handling
    }
  },

  /**
   * Update an existing invoice
   * @param {string} id - Invoice ID
   * @param {Object} data - Updated invoice data
   * @returns {Promise} API response with updated invoice
   */
  updateInvoice: async (id, data) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const response = await apiClient.put(`/invoices/${id}`, data);
    return response.data;
  },

  /**
   * Update invoice status
   * @param {string} id - Invoice ID
   * @param {string} status - New status
   * @returns {Promise} API response with updated invoice
   */
  updateInvoiceStatus: async (id, status) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    if (!status) {
      throw new Error('Status is required');
    }
    const response = await apiClient.put(`/invoices/${id}/status`, { status });
    return response.data;
  },

  /**
   * Void an invoice
   * @param {string} id - Invoice ID
   * @returns {Promise} API response with updated invoice
   */
  voidInvoice: async (id) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const response = await apiClient.post(`/invoices/${id}/void`);
    return response.data;
  },

  /**
   * Duplicate an invoice
   * @param {string} id - Invoice ID to duplicate
   * @returns {Promise} API response with new invoice
   */
  duplicateInvoice: async (id) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const response = await apiClient.post(`/invoices/${id}/duplicate`);
    return response.data;
  },

  /**
   * Get payments for an invoice
   * @param {string} id - Invoice ID
   * @returns {Promise} API response with payments array
   */
  getInvoicePayments: async (id) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const response = await apiClient.get(`/invoices/${id}/payments`);
    return response.data;
  },

  /**
   * Record a payment for an invoice
   * @param {string} id - Invoice ID
   * @param {Object} paymentData - Payment details
   * @returns {Promise} API response with payment and updated invoice
   */
  recordPayment: async (id, paymentData, { idempotencyKey } = {}) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
    const response = await apiClient.post(`/invoices/${id}/payments`, paymentData, { headers });
    return response.data;
  },

  /**
   * Delete an invoice
   * @param {string} id - Invoice ID
   * @returns {Promise} API response
   */
  deleteInvoice: async (id) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const response = await apiClient.delete(`/invoices/${id}`);
    return response.data;
  },

  /**
   * Send invoice email
   * @param {string} id - Invoice number (e.g., INV-0042)
   * @param {Object} [options] - Email options
   * @param {string} [options.toEmail] - Override recipient email
   * @param {string[]} [options.ccEmails] - CC addresses
   * @param {string[]} [options.bccEmails] - BCC addresses
   * @param {string} [options.subject] - Email subject
   * @param {string} [options.body] - Email body text
   * @param {boolean} [options.sendCopy] - Send copy to sender
   * @returns {Promise} API response
   */
  sendInvoice: async (id, options = {}) => {
    if (!id) {
      throw new Error('Invoice ID is required');
    }
    const response = await apiClient.post(`/invoices/${id}/send`, options);
    return response.data;
  },

  /**
   * Send a payment receipt email for a paid invoice
   * @param {string} invoiceNumber - Invoice number
   * @param {Object} options - Email options (toEmail, ccEmails, bccEmails, subject, body, sendCopy)
   * @returns {Promise} API response
   */
  sendReceipt: async (invoiceNumber, options = {}) => {
    if (!invoiceNumber) {
      throw new Error('Invoice number is required');
    }
    const response = await apiClient.post(`/invoices/${invoiceNumber}/send-receipt`, options);
    return response.data;
  },

  /**
   * Send a payment reminder email for an overdue/outstanding invoice
   * @param {string} invoiceNumber - Invoice number
   * @param {Object} options - Email options (toEmail, ccEmails, bccEmails, subject, body, sendCopy)
   * @returns {Promise} API response
   */
  sendReminder: async (invoiceNumber, options = {}) => {
    if (!invoiceNumber) {
      throw new Error('Invoice number is required');
    }
    const response = await apiClient.post(`/invoices/${invoiceNumber}/send-reminder`, options);
    return response.data;
  },

  /**
   * Get invoice PDF as a blob for preview/download
   * @param {string} invoiceNumber - Invoice number (e.g., INV-0042)
   * @returns {Promise<Blob>} PDF blob
   */
  getPdfBlob: async (invoiceNumber) => {
    if (!invoiceNumber) {
      throw new Error('Invoice number is required');
    }
    const response = await apiClient.get(`/invoices/${invoiceNumber}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Format invoice status for display
   * @param {string} status - Invoice status
   * @returns {string} Formatted status
   */
  formatStatus: (status) => {
    const statusMap = {
      'open': 'Open',
      'void': 'Void'
    };
    return statusMap[status] || status;
  },


  /**
   * Calculate if invoice is overdue
   * @param {string} dueDate - Due date
   * @param {string} paymentStatus - Payment status
   * @param {string} [timeZone] - Company primary-location IANA TZ
   * @returns {boolean} True if overdue
   */
  isOverdue: (dueDate, paymentStatus, timeZone) => {
    if (!dueDate || !paymentStatus) return false;
    if (paymentStatus === 'paid' || paymentStatus === 'voided') return false;
    if (paymentStatus !== 'unpaid' && paymentStatus !== 'partially_paid') return false;
    return isInvoiceOverdue(dueDate, paymentStatus, timeZone);
  },

  /**
   * Format currency
   * @param {number} amount - Amount to format
   * @returns {string} Formatted amount
   */
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  },

  /**
   * Get payment method display name
   * @param {string} method - Payment method
   * @returns {string} Display name
   */
  getPaymentMethodName: (method) => {
    const methods = {
      'card': 'Credit Card',
      'credit_card': 'Credit Card',
      'debit_card': 'Debit Card',
      'ach': 'Bank Transfer',
      'wire': 'Bank Transfer',
      'bank_transfer': 'Bank Transfer',
      'cash': 'Cash',
      'check': 'Check',
      'external': 'Other',
      'other': 'Other'
    };
    return methods[method] || method;
  },

  /**
   * Get generic invoice line item types
   * @returns {Array} Array of generic item options
   */
  getGenericItemTypes: () => {
    return [
      { value: 'sales', label: 'Sales', type: 'product' },
      { value: 'service', label: 'Service', type: 'service' },
      { value: 'shipping', label: 'Shipping', type: 'shipping' },
      { value: 'other', label: 'Other', type: 'other' }
    ];
  }
,
  
  /**
   * Generate portal link for invoice
   * @param {string} invoiceId - Invoice ID
   * @param {string} customerId - Customer ID
   * @returns {Promise} API response with portal URL and token
   */
  generatePortalLink: async (invoiceId, customerId) => {
    const response = await apiClient.post('/portal/generate-token', {
      invoiceId,
      customerId,
      type: 'invoice'
    });
    return response.data;
  },

  /**
   * Get invoice history events
   * @param {string} invoiceNumber - Invoice number (e.g., INV-0027)
   * @returns {Promise} API response with history events
   */
  getInvoiceHistory: async (invoiceNumber) => {
    if (!invoiceNumber) {
      throw new Error('Invoice number is required');
    }
    
    try {
      const response = await apiClient.get(`/invoices/${invoiceNumber}/history`);
      
      // Transform backend history events to match frontend expectations
      if (response.data.success && response.data.history) {
        const transformedHistory = response.data.history.map(event => ({
          id: event.id,
          action: event.action,
          actionDate: event.action_date,
          actionDetails: event.action_details || {},
          performedBy: event.performed_by_clerk_id || event.performed_by,
          performedByName: event.performed_by_first_name && event.performed_by_last_name 
            ? `${event.performed_by_first_name} ${event.performed_by_last_name}`.trim()
            : null,
          source: event.source,
          transactionId: event.transaction_id,
          notes: event.notes,
          ipAddress: event.ip_address,
          userAgent: event.user_agent
        }));
        
        return {
          success: true,
          history: transformedHistory
        };
      }
      
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch invoice history', {
        operation: 'invoiceService.getInvoiceHistory',
        invoiceNumber,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      
      // Return empty history on error to prevent UI breaks
      return {
        success: false,
        history: [],
        error: error.message
      };
    }
  }
};

export default invoiceService;
